package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.model.Fact
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.Joke
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.Story
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn

/** Hub state: section counts + the "Your saved gems" rail resolved against the live corpora. */
data class GemsHubUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val jokeCount: Int = 0,
    val recipeCount: Int = 0,
    val factCount: Int = 0,
    val taleCount: Int = 0,
    val poetCount: Int = 0,
    val storyCount: Int = 0,
    /** Hafez poems that carry a fal reading — the pill on the Fal-e Hafez card. */
    val falCount: Int = 0,
    /** Saved gems resolved to displayable content (order: recipes then jokes, as saved). */
    val savedJokes: List<Joke> = emptyList(),
    val savedRecipes: List<Recipe> = emptyList(),
    val savedFacts: List<Fact> = emptyList(),
    val savedTales: List<Tale> = emptyList(),
    val savedPoems: List<Poem> = emptyList(),
    val savedStories: List<Story> = emptyList(),
)

/**
 * Hidden Gems hub (`/gems`): streams both section corpora for the item-count pills and resolves
 * the private saved-gems set against them for the saved rail. Counts use the clean jokes tier only
 * — the hub is reachable by guests and the adult tier must stay invisible there.
 */
class GemsHubViewModel(
    gemsRepository: GemsRepository,
    savedController: GemsSavedController,
) : ViewModel() {

    val state: StateFlow<GemsHubUiState> =
        combine(
            gemsRepository.observeJokes(includeAdult = false),
            gemsRepository.observeRecipes(),
            gemsRepository.observeFacts(),
            gemsRepository.observeTales(),
            savedController.saved,
        ) { jokesResult, recipesResult, factsResult, talesResult, saved ->
            if (jokesResult is DataResult.Failure &&
                recipesResult is DataResult.Failure &&
                factsResult is DataResult.Failure &&
                talesResult is DataResult.Failure
            ) {
                GemsHubUiState(loading = false, error = true)
            } else {
                val jokes = (jokesResult as? DataResult.Success)?.data.orEmpty()
                val recipes = (recipesResult as? DataResult.Success)?.data.orEmpty()
                val facts = (factsResult as? DataResult.Success)?.data.orEmpty()
                val tales = (talesResult as? DataResult.Success)?.data.orEmpty()
                val savedJokeIds = saved.filter { it.type == GemType.Joke }.map { it.id }.toSet()
                val savedRecipeIds = saved.filter { it.type == GemType.Recipe }.map { it.id }.toSet()
                val savedFactIds = saved.filter { it.type == GemType.Fact }.map { it.id }.toSet()
                val savedTaleIds = saved.filter { it.type == GemType.Tale }.map { it.id }.toSet()
                GemsHubUiState(
                    loading = false,
                    jokeCount = jokes.size,
                    recipeCount = recipes.size,
                    factCount = facts.size,
                    taleCount = tales.size,
                    savedJokes = jokes.filter { it.id in savedJokeIds },
                    savedRecipes = recipes.filter { it.id in savedRecipeIds },
                    savedFacts = facts.filter { it.id in savedFactIds },
                    savedTales = tales.filter { it.id in savedTaleIds },
                )
            }
        }
            // Poetry + stories ride an OUTER combine: the typed `combine` overloads stop at five
            // sources, and the hub already uses all five. Their counts only fill section pills, so a
            // failure there leaves the rest of the hub intact rather than erroring the whole screen.
            .let { base ->
                combine(
                    base,
                    gemsRepository.observePoets(),
                    gemsRepository.observePoems(),
                    gemsRepository.observeStories(),
                    savedController.saved,
                ) { state, poetsResult, poemsResult, storiesResult, saved ->
                    val poems = (poemsResult as? DataResult.Success)?.data.orEmpty()
                    val stories = (storiesResult as? DataResult.Success)?.data.orEmpty()
                    val savedPoemIds = saved.filter { it.type == GemType.Poem }.map { it.id }.toSet()
                    val savedStoryIds = saved.filter { it.type == GemType.Story }.map { it.id }.toSet()
                    state.copy(
                        poetCount = (poetsResult as? DataResult.Success)?.data?.size ?: 0,
                        storyCount = stories.size,
                        falCount = poems.count { it.poetId == HAFEZ_POET_ID && it.fal != null },
                        savedPoems = poems.filter { it.id in savedPoemIds },
                        savedStories = stories.filter { it.id in savedStoryIds },
                    )
                }
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), GemsHubUiState())
}
